import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Switch from 'antd/lib/switch';
import Checkbox from 'antd/lib/checkbox';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import {
    selectRequiresFixRequestsApprovals,
    selectRequiredFixRequestsApprovals,
    selectVCSSupportsStatusChecks,
    selectEnableStatusChecksForFixRequests,
    selectResetFixApprovalOnNewCommit,
    selectFixApprovalMode,
    selectFixApprovalWindowTrigger,
    selectFixApprovalRequireForPreWindowFixes,
    selectFixApprovalRequiredApproverUserGroupIds,
    selectFixApprovalRequiredApproverUserIds,
    selectFixApprovalDisallowCodeAuthor,
    selectFixApprovalImplicitCreatorApproval,
    selectOrgSettings,
    selectOrgUsers,
} from '@/selectors/selectors';
import { InputNumber, Radio, Select, Space, Tooltip } from 'antd/lib';
import { useWatch } from 'antd/es/form/Form';
import { antGrey } from '../styles/globalStyles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useFixSettingsModal } from './useFixSettingsModal';
import { RelativeTimeTriggerSettings } from '../RelativeTimeTriggerSettings';
import { FixApprovalMode, RelativeTimeTrigger } from '@/types';
import { RootState } from '@/reducers/reducers';

interface EditFixApprovalsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditFixApprovalsModal: React.FC<EditFixApprovalsModalProps> = (props: EditFixApprovalsModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiredFixRequestsApprovals = useSelector(selectRequiredFixRequestsApprovals(props.appId));
    const vcsSupportsStatusChecks = useSelector(selectVCSSupportsStatusChecks(props.appId, props.orgId));
    const enableStatusChecksForFixRequests = useSelector(selectEnableStatusChecksForFixRequests(props.appId));
    const resetFixApprovalOnNewCommit = useSelector(selectResetFixApprovalOnNewCommit(props.appId));
    const fixApprovalMode = useSelector(selectFixApprovalMode(props.appId));
    const fixApprovalWindowTrigger = useSelector(selectFixApprovalWindowTrigger(props.appId));
    const fixApprovalRequireForPreWindowFixes = useSelector(selectFixApprovalRequireForPreWindowFixes(props.appId));
    const fixApprovalRequiredApproverUserGroupIds = useSelector(
        selectFixApprovalRequiredApproverUserGroupIds(props.appId),
    );
    const fixApprovalRequiredApproverUserIds = useSelector(selectFixApprovalRequiredApproverUserIds(props.appId));
    const fixApprovalDisallowCodeAuthor = useSelector(selectFixApprovalDisallowCodeAuthor(props.appId));
    const fixApprovalImplicitCreatorApproval = useSelector(selectFixApprovalImplicitCreatorApproval(props.appId));

    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, props.orgId));
    const orgGroups = orgSettings?.groups ?? {};
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));

    const isRequireApprovalsEnabled = useWatch('requireApprovals', form);
    const selectedApprovalMode = useWatch('approvalMode', form);
    const hasSpecificApprovers = useWatch('hasSpecificApprovers', form);
    const isStatusChecksEnabled = useWatch('enableStatusChecks', form);

    const hasExistingApprovers =
        (fixApprovalRequiredApproverUserGroupIds?.length ?? 0) > 0 ||
        (fixApprovalRequiredApproverUserIds?.length ?? 0) > 0;

    const initialValues = {
        requireApprovals: requiresFixRequestsApprovals,
        requiredApprovals: requiredFixRequestsApprovals,
        approvalMode: fixApprovalMode || 'always',
        enableStatusChecks: enableStatusChecksForFixRequests,
        resetApprovalOnNewCommit: resetFixApprovalOnNewCommit,
        hasSpecificApprovers: hasExistingApprovers,
        requiredApproverUserGroupIds: fixApprovalRequiredApproverUserGroupIds ?? [],
        requiredApproverUserIds: fixApprovalRequiredApproverUserIds ?? [],
        fixApprovalWindowTrigger: fixApprovalWindowTrigger ?? {},
        requireForPreWindowFixes: fixApprovalRequireForPreWindowFixes,
        disallowCodeAuthor: fixApprovalDisallowCodeAuthor,
        implicitCreatorApproval: fixApprovalImplicitCreatorApproval,
    };

    const onFinish = (value) => {
        const approvalMode: FixApprovalMode = value.approvalMode || FixApprovalMode.Always;
        const windowTrigger: RelativeTimeTrigger | undefined =
            approvalMode === FixApprovalMode.Window ? { ...value.fixApprovalWindowTrigger, enabled: true } : undefined;

        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            requiresFixRequestsApprovals: value.requireApprovals,
            requiredFixRequestsApprovals: value.requiredApprovals || 1,
            fixApprovalMode: approvalMode,
            fixApprovalWindowTrigger: windowTrigger,
            fixApprovalRequireForPreWindowFixes:
                approvalMode === FixApprovalMode.Window && value.requireForPreWindowFixes,
            fixApprovalRequiredApproverUserGroupIds:
                value.hasSpecificApprovers ? value.requiredApproverUserGroupIds : [],
            fixApprovalRequiredApproverUserIds: value.hasSpecificApprovers ? value.requiredApproverUserIds : [],
            enableStatusChecksForFixRequests: value.requireApprovals && value.enableStatusChecks,
            resetFixApprovalOnNewCommit: value.requireApprovals && value.resetApprovalOnNewCommit,
            fixApprovalDisallowCodeAuthor: value.requireApprovals && value.disallowCodeAuthor,
            fixApprovalImplicitCreatorApproval:
                value.requireApprovals && value.hasSpecificApprovers && value.implicitCreatorApproval,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    const approverOptions = React.useMemo(() => {
        const groupOptions = Object.values(orgGroups).map((group) => ({
            label: `Group: ${group.name}`,
            value: `group:${group.id}`,
        }));
        const userOptions = orgUsers.map((user) => ({
            label: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
            value: `user:${user.id}`,
        }));
        return [...groupOptions, ...userOptions];
    }, [orgGroups, orgUsers]);

    const [selectedApproverValues, setSelectedApproverValues] = React.useState<string[]>(() => {
        const groupValues = (fixApprovalRequiredApproverUserGroupIds ?? []).map((id: string) => `group:${id}`);
        const userValues = (fixApprovalRequiredApproverUserIds ?? []).map((id: string) => `user:${id}`);
        return [...groupValues, ...userValues];
    });

    const handleApproverChange = (selectedValues: string[]) => {
        setSelectedApproverValues(selectedValues);
        const groupIds = selectedValues.filter((v) => v.startsWith('group:')).map((v) => v.replace('group:', ''));
        const userIds = selectedValues.filter((v) => v.startsWith('user:')).map((v) => v.replace('user:', ''));
        form.setFieldValue('requiredApproverUserGroupIds', groupIds);
        form.setFieldValue('requiredApproverUserIds', userIds);
    };

    return (
        <Modal
            title="Fix approvals"
            onOk={form.submit}
            okButtonProps={{ loading: isLoading }}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={afterClose}
            destroyOnClose
            width={640}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Require fix approvals from team members with sufficient privileges. Approvals can be performed on
                    the Feature readiness step.
                </Text>
                <Form
                    onFinish={onFinish}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item name="requireApprovals" valuePropName="checked" label="Require fix approvals">
                        <Switch />
                    </Form.Item>

                    {isRequireApprovalsEnabled && (
                        <>
                            <Form.Item name="approvalMode" style={{ marginBottom: 16 }}>
                                <Radio.Group>
                                    <Space direction="vertical">
                                        <Radio value="always">Always require fix approvals</Radio>
                                        <Radio value="window">Require fix approvals during specific windows only</Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>

                            {selectedApprovalMode === 'window' && (
                                <Space style={{ marginLeft: 24, marginBottom: 16 }} direction="vertical" size={16}>
                                    <Form.Item
                                        name="requireForPreWindowFixes"
                                        label="For fix requests that are..."
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Radio.Group>
                                            <Space direction="vertical">
                                                <Radio value={true}>
                                                    Created, or unmerged
                                                    <Tooltip title="Fixes that were created earlier but still unmerged when the window begins will require approval.">
                                                        <InfoCircleOutlined
                                                            style={{ color: antGrey.grey7, marginLeft: 8 }}
                                                        />
                                                    </Tooltip>
                                                </Radio>
                                                <Radio value={false}>
                                                    Created
                                                    <Tooltip title="Fixes that were created before the specified window will never require approvals.">
                                                        <InfoCircleOutlined
                                                            style={{ color: antGrey.grey7, marginLeft: 8 }}
                                                        />
                                                    </Tooltip>
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Form.Item>
                                    <RelativeTimeTriggerSettings
                                        orgId={props.orgId}
                                        appId={props.appId}
                                        stepConfigId={null}
                                        initialValues={fixApprovalWindowTrigger ?? {}}
                                        parentFieldName="fixApprovalWindowTrigger"
                                        label="Starting"
                                    />
                                </Space>
                            )}

                            <Form.Item name="requiredApprovals" label="Number of approvals required">
                                <InputNumber
                                    min={1}
                                    onBlur={() => {
                                        if (form.getFieldValue('requiredApprovals') == null) {
                                            form.setFieldValue('requiredApprovals', 1);
                                        }
                                    }}
                                />
                            </Form.Item>

                            <Form.Item name="hasSpecificApprovers" valuePropName="checked">
                                <Checkbox>Set specific user groups/individuals required to approve</Checkbox>
                            </Form.Item>

                            {hasSpecificApprovers && (
                                <>
                                    <Form.Item name="requiredApproverUserGroupIds" hidden />
                                    <Form.Item name="requiredApproverUserIds" hidden />
                                    <Form.Item label="Required approvers" style={{ marginLeft: 24, marginTop: -12 }}>
                                        <Select
                                            mode="multiple"
                                            placeholder="Select user groups or individuals"
                                            options={approverOptions}
                                            value={selectedApproverValues}
                                            onChange={handleApproverChange}
                                            filterOption={(input, option) =>
                                                (option?.label?.toString() ?? '')
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="implicitCreatorApproval"
                                        valuePropName="checked"
                                        style={{ marginLeft: 24 }}
                                    >
                                        <Checkbox>
                                            Provide implicit approval if a required approver creates a fix request
                                            <Tooltip title="If the fix request creator is a required approver, or a member of a required approver user group, their approval will be added automatically.">
                                                <InfoCircleOutlined
                                                    style={{
                                                        color: antGrey.grey7,
                                                        marginLeft: 8,
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                />
                                            </Tooltip>
                                        </Checkbox>
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item name="disallowCodeAuthor" valuePropName="checked">
                                <Checkbox>
                                    Prevent code authors from approving their own fixes
                                    <Tooltip title="If the 'Provide implicit approval...' option above is enabled when setting specific user groups/individuals as approvers, code authors will always be allowed to approve their own fixes regardless of this setting.">
                                        <InfoCircleOutlined
                                            style={{
                                                color: antGrey.grey7,
                                                marginLeft: 8,
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Tooltip>
                                </Checkbox>
                            </Form.Item>

                            <Form.Item
                                name="enableStatusChecks"
                                hidden={!vcsSupportsStatusChecks}
                                valuePropName="checked"
                            >
                                <Checkbox>
                                    Add pull request status check for fix request approvals
                                    <Tooltip title="Runway will include a status check on any pull requests created for fixes going into the release branch. The status check will only pass when the fix request associated with the PR has been approved.">
                                        <InfoCircleOutlined
                                            style={{
                                                color: antGrey.grey7,
                                                marginLeft: 8,
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Tooltip>
                                </Checkbox>
                            </Form.Item>

                            <Form.Item
                                name="resetApprovalOnNewCommit"
                                valuePropName="checked"
                                extra={
                                    !(vcsSupportsStatusChecks && isStatusChecksEnabled) && (
                                        <Text type="secondary">
                                            Note that without status checks enabled, this will not prevent the PR from
                                            being merged.
                                        </Text>
                                    )
                                }
                            >
                                <Checkbox>
                                    Reset fix approvals when a new commit is pushed
                                    <Tooltip title="When a pull request for an approved fix receives a new commit, the fix request goes back to pending approval. If status checks are enabled, the status check also reverts to pending.">
                                        <InfoCircleOutlined
                                            style={{
                                                color: antGrey.grey7,
                                                marginLeft: 8,
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Tooltip>
                                </Checkbox>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default EditFixApprovalsModal;
